import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { getNotifications } from '../services/api';

type NotificationUnreadContextValue = {
  unreadCount: number;
  refreshUnread: () => Promise<number>;
  setUnreadCount: (count: number) => void;
  decrementUnread: (by?: number) => void;
  clearUnread: () => void;
};

const NotificationUnreadContext = createContext<NotificationUnreadContextValue | null>(null);

export function NotificationUnreadProvider({ children }: { children: ReactNode }) {
  const [unreadCount, setUnreadCountState] = useState(0);

  const setUnreadCount = useCallback((count: number) => {
    setUnreadCountState(Math.max(0, count));
  }, []);

  const decrementUnread = useCallback((by = 1) => {
    setUnreadCountState((prev) => Math.max(0, prev - by));
  }, []);

  const clearUnread = useCallback(() => {
    setUnreadCountState(0);
  }, []);

  const refreshUnread = useCallback(async () => {
    try {
      const result = await getNotifications({ page: 1, limit: 1 });
      const count = Number(result.unreadCount) || 0;
      setUnreadCountState(count);
      return count;
    } catch {
      return 0;
    }
  }, []);

  useEffect(() => {
    void refreshUnread();
    const intervalId = window.setInterval(() => {
      void refreshUnread();
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, [refreshUnread]);

  const value = useMemo(
    () => ({
      unreadCount,
      refreshUnread,
      setUnreadCount,
      decrementUnread,
      clearUnread,
    }),
    [unreadCount, refreshUnread, setUnreadCount, decrementUnread, clearUnread],
  );

  return (
    <NotificationUnreadContext.Provider value={value}>{children}</NotificationUnreadContext.Provider>
  );
}

export function useNotificationUnread() {
  const context = useContext(NotificationUnreadContext);
  if (!context) {
    throw new Error('useNotificationUnread must be used within NotificationUnreadProvider');
  }
  return context;
}

/** Applique le compteur renvoyé par l’API après marquage lu, avec repli sur refresh. */
export async function syncUnreadCountAfterRead(
  ctx: Pick<NotificationUnreadContextValue, 'setUnreadCount' | 'refreshUnread'>,
  unreadCount?: number | null,
) {
  if (typeof unreadCount === 'number' && Number.isFinite(unreadCount)) {
    ctx.setUnreadCount(unreadCount);
    return;
  }
  await ctx.refreshUnread();
}
